import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged-out, object = logged-in
  const [favorites, setFavorites] = useState(new Set());

  const refreshFavorites = useCallback(async () => {
    try {
      const { data } = await api.get("/favorites");
      setFavorites(new Set(data.ids || []));
    } catch {
      setFavorites(new Set());
    }
  }, []);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await refreshFavorites();
    } catch {
      setUser(false);
      setFavorites(new Set());
    }
  }, [refreshFavorites]);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      await refreshFavorites();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (name, email, password) => {
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      setUser(data);
      await refreshFavorites();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
    setFavorites(new Set());
  };

  const toggleFavorite = async (cafeId) => {
    if (!user) return { ok: false, error: "Please sign in to save favorites." };
    try {
      const { data } = await api.post(`/favorites/${cafeId}/toggle`);
      setFavorites((prev) => {
        const next = new Set(prev);
        if (data.favorited) next.add(cafeId);
        else next.delete(cafeId);
        return next;
      });
      return { ok: true, favorited: data.favorited };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, favorites, login, register, logout, toggleFavorite, refreshFavorites }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
